import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ClipboardList, Hash, Building2, Shield, FileText, Hammer, Calendar } from "lucide-react";
import { ClaimDetailed } from "@/api/generated/client/models";
import { EditableField } from "@/components/ui/editable-field";
import { useSidebarValidation } from "@/hooks/use-sidebar-validation";
import { formatDate } from "@/lib/utils";

interface SidebarClaimSectionProps {
    claim: ClaimDetailed;
    onUpdate: (field: string, value: any) => Promise<void>;
    canWrite: boolean;
}

export function SidebarClaimSection({ claim, onUpdate, canWrite }: SidebarClaimSectionProps) {
    const { validateAndSave } = useSidebarValidation();

    return (
        <Card className="shadow-xs bg-card">
            <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-primary" />
                    <h2 className="text-sm font-semibold">Claim Information</h2>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                    <Hash className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Claim Number</p>
                        <EditableField
                            value={claim.claimNumber}
                            onSave={(val) => validateAndSave('claimNumber', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium break-words whitespace-pre-wrap h-auto"
                            placeholder="Claim #"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Policy Number</p>
                        <EditableField
                            value={claim.policyNumber}
                            onSave={(val) => validateAndSave('policyNumber', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium break-words whitespace-pre-wrap h-auto"
                            placeholder="Add Policy #"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Insurance Company</p>
                        <EditableField
                            value={claim.insuranceCarrier}
                            onSave={(val) => validateAndSave('insuranceCarrier', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium break-words whitespace-pre-wrap h-auto"
                            placeholder="Add Insurance Company"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Hammer className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Contractor</p>
                        <EditableField
                            value={claim.contractor ?? ''}
                            onSave={(val) => validateAndSave('contractor', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium break-words whitespace-pre-wrap h-auto"
                            placeholder="Add Contractor"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Internal File Number</p>
                        <EditableField
                            value={claim.internalFileNumber}
                            onSave={(val) => validateAndSave('internalFileNumber', val, onUpdate)}
                            disabled={!canWrite}
                            className="text-sm font-medium break-words whitespace-pre-wrap h-auto"
                            placeholder="Add File Number"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground mb-1">Created</p>
                        <p className="text-sm font-medium">{formatDate(claim.createdAt)}</p>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

